import { selectUserContext } from 'core/userContext';
import { ReferencesDrawer } from 'modules/customEvents/components/ReferencesDrawer';
import { UpdateCustomEventSettingsModals } from 'modules/customEvents/components/UpdateCustomEventSettingsModals';
import {
  CustomEventsQueryContextProvider,
  DrawersContextProvider,
  FiltersContextProvider,
  SelectionContextProvider,
} from 'modules/customEvents/contexts';
import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router';
import styled from 'styled-components';
import { PageHeader } from 'ui/pageHeader';
import { NavTabTrigger, NavTabsList, TabsContent, TabsGroup } from 'ui/tabsGroup';
import { colors, spacing } from 'ui/theme';
import { FullWidth } from 'ui/utility/fullWidth';
import { ModalContextProvider } from 'utils/contexts/modal';
import { PaginationContextProvider } from 'utils/contexts/pagination';
import { SortingContextProvider } from 'utils/contexts/sorting';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import {
  CONTENT_SECTIONS,
  DATA_SCHEMA_MANAGEMENT_URL,
  SORTING_CONTEXT_INITIAL_STATE,
} from '../constants';
import {
  CustomEventDetailsContextProvider,
  SearchParamsContextProvider,
  UserMappingsContextProvider,
  useSearchParamsContext,
} from '../contexts';
import { PurchaseMappingsContextProvider } from '../contexts/PurchaseMappingsContextProvider';
import { CustomEventDetailsPage } from './CustomEventDetailsPage';
import { UserProfileModals } from './UserProfileModals';
import { CustomEventsContent } from './customEvents';
import { PurchaseFieldsContent } from './purchaseFields/PurchaseFieldsContent';
import { SettingsContent } from './settings/SettingsContent';
import { UserProfileFieldsContent } from './userProfileFields';

const { ManageProjectSettings } = PROJECT_PERMISSIONS;

const DataDictionaryBody = styled(FullWidth)`
  flex-grow: 1;
  padding: ${spacing.base} ${spacing.layoutLRPadding};
  background-color: ${colors.backgroundSecondary};
`;

export const StyledNavTabsList = styled(NavTabsList)`
  margin-left: -${spacing.threeQuarter};
  border-bottom: none;
`;

const StyledTabsGroup = styled(TabsGroup)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

export const DATA_DICTIONARY_PAGE_TEXT = {
  pageTitle: 'Data Schema Management',
  navTabsListTestId: 'nav-tabs-list',
  tabsContentTestId: 'tabs-content-container',
};

/**
 * Acts as a Context Provider for NavTabs
 */
export const NavTabsGroup = ({ children }: { children: ReactNode }) => {
  const { contentSection, setContentSection, resetSearchParams } = useSearchParamsContext();

  const handleValueChange = (value: string) => {
    setContentSection(value);
    resetSearchParams();
  };

  return (
    <StyledTabsGroup
      data-test="tabs-group"
      onValueChange={handleValueChange}
      defaultValue={contentSection}
      value={contentSection}
    >
      {children}
    </StyledTabsGroup>
  );
};

export const DataDictionary = () => {
  return (
    <>
      <PageHeader>{DATA_DICTIONARY_PAGE_TEXT.pageTitle}</PageHeader>
      <StyledNavTabsList data-test={DATA_DICTIONARY_PAGE_TEXT.navTabsListTestId}>
        <NavTabTrigger value={CONTENT_SECTIONS.customEvents}>Custom events</NavTabTrigger>
        <NavTabTrigger value={CONTENT_SECTIONS.userFields}>User profile fields</NavTabTrigger>
        <NavTabTrigger value={CONTENT_SECTIONS.purchaseFields}>Purchase fields</NavTabTrigger>
        <NavTabTrigger value={CONTENT_SECTIONS.settings}>Settings</NavTabTrigger>
      </StyledNavTabsList>
      <DataDictionaryBody data-test="data-dictionary-body">
        <TabsContent data-test="custom-events-content" value={CONTENT_SECTIONS.customEvents}>
          <CustomEventsContent />
        </TabsContent>
        <TabsContent data-test="user-fields-content" value={CONTENT_SECTIONS.userFields}>
          <UserProfileFieldsContent />
        </TabsContent>
        <TabsContent data-test="purchase-fields-content" value={CONTENT_SECTIONS.purchaseFields}>
          <PurchaseFieldsContent />
        </TabsContent>
        <TabsContent data-test="settings-content" value={CONTENT_SECTIONS.settings}>
          <SettingsContent />
        </TabsContent>
      </DataDictionaryBody>
    </>
  );
};

/** Common components shared between DataDictionary and Custom Event Mappings pages */
const DataDictionaryAccessories = () => {
  const { setIngestionPolicy } = useSearchParamsContext();

  return (
    <>
      <UserProfileModals />
      <ReferencesDrawer />
      <UpdateCustomEventSettingsModals setIngestionPolicyFilter={setIngestionPolicy} />
    </>
  );
};

/** Smart Ingest page, requires Manage Project Settings permissions */
export const DataDictionaryPage = () => {
  const { rbacPermissions } = useSelector(selectUserContext);
  const hasProjectSettingsPermissions = rbacPermissions?.includes(ManageProjectSettings);

  return (
    <>
      {hasProjectSettingsPermissions && (
        <UserMappingsContextProvider>
          <PurchaseMappingsContextProvider>
            <CustomEventsQueryContextProvider>
              <CustomEventDetailsContextProvider>
                <SelectionContextProvider>
                  <SortingContextProvider
                    defaultSortField={SORTING_CONTEXT_INITIAL_STATE.sortBy}
                    defaultSortDirection={SORTING_CONTEXT_INITIAL_STATE.sortDirection}
                  >
                    <FiltersContextProvider>
                      <PaginationContextProvider>
                        <SearchParamsContextProvider>
                          <ModalContextProvider>
                            <DrawersContextProvider>
                              <NavTabsGroup>
                                <Switch>
                                  <Route exact path={DATA_SCHEMA_MANAGEMENT_URL}>
                                    <DataDictionary />
                                  </Route>
                                  <Route
                                    exact
                                    path={`${DATA_SCHEMA_MANAGEMENT_URL}/customEvent/:customEventName`}
                                  >
                                    <CustomEventDetailsPage />
                                  </Route>
                                </Switch>
                                <DataDictionaryAccessories />
                              </NavTabsGroup>
                            </DrawersContextProvider>
                          </ModalContextProvider>
                        </SearchParamsContextProvider>
                      </PaginationContextProvider>
                    </FiltersContextProvider>
                  </SortingContextProvider>
                </SelectionContextProvider>
              </CustomEventDetailsContextProvider>
            </CustomEventsQueryContextProvider>
          </PurchaseMappingsContextProvider>
        </UserMappingsContextProvider>
      )}
    </>
  );
};
